import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import * as PleasedApi from '@/common/api/api.pleased';
import type { VoiceAiAvailability } from '@/common/api/models.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import type { UseConnectQueryOptions } from './connectQuery.types';

const VOICE_AI_UNAVAILABLE_FALLBACK: VoiceAiAvailability = { available: false };

export const VOICE_AI_AVAILABILITY_QUERY_KEY = ['voiceAiAvailability'];

/**
 * TanStack Query hook to fetch Landing Voice AI session availability.
 */
export const useVoiceAiAvailability = (options?: UseConnectQueryOptions) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive } = storeToRefs(mockedFlowStore);
  const enabled = computed(() => options?.enabled?.value ?? true);

  return useQuery({
    queryKey: VOICE_AI_AVAILABILITY_QUERY_KEY,
    queryFn: async (): Promise<VoiceAiAvailability> => {
      if (isMockedFlowActive.value) {
        return VOICE_AI_UNAVAILABLE_FALLBACK;
      }

      return await PleasedApi.getVoiceAiAvailability().catch(() => VOICE_AI_UNAVAILABLE_FALLBACK);
    },
    enabled,
    staleTime: 0,
    refetchInterval: 30 * 1000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  });
};
